import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const services = [
  { name: 'Flight Booking',    slug: 'flight-booking',            icon: '✈️', blurb: 'Budget-friendly fares on domestic and international routes.', color: '#3F72AF' },
  { name: 'Hotel Booking',     slug: 'hotel-booking',             icon: '🏨', blurb: 'Luxury and comfortable stays, handpicked for every budget.',   color: '#2d6a9f' },
  { name: 'Holiday Packages',  slug: 'holiday-packages',          icon: '🧳', blurb: 'Custom itineraries built entirely around you.',                color: '#1a5c91' },
  { name: 'Visa Assistance',   slug: 'visa-assistance',           icon: '🛂', blurb: 'End-to-end documentation support, simplified.',               color: '#3F72AF' },
  { name: 'Travel Insurance',  slug: 'travel-insurance',          icon: '🛡️', blurb: 'Comprehensive coverage so you travel worry-free.',             color: '#2d6a9f' },
  { name: 'Airport Transfer',  slug: 'airport-transfer',          icon: '🚗', blurb: 'Reliable on-time pickups and drop-offs.',                     color: '#1a5c91' },
  { name: 'Cruise Packages',   slug: 'cruise-packages',           icon: '🚢', blurb: 'Curated cruise routes across stunning global waters.',         color: '#3F72AF' },
  { name: 'Corporate Travel',  slug: 'corporate-travel-services', icon: '💼', blurb: 'Dependable travel management for teams of any size.',          color: '#2d6a9f' },
];

const VISIBLE   = 3;
const TOTAL     = Math.ceil(services.length / VISIBLE); // 3 slides
const AUTO_MS   = 2500;

// One card — 3-D tilt on mouse move, shimmer on hover
function ServiceCard({ service, index, slideEntering, direction }) {
  const cardRef   = useRef(null);
  const frameRef  = useRef(null);
  const [tilt, setTilt]       = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const [shimPos, setShimPos] = useState(-100);

  // staggered entrance animation
  const delay = index * 80;

  const handleMouseMove = (e) => {
    const el   = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx   = rect.left + rect.width  / 2;
    const cy   = rect.top  + rect.height / 2;
    const dx   = (e.clientX - cx) / (rect.width  / 2);
    const dy   = (e.clientY - cy) / (rect.height / 2);
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() =>
      setTilt({ x: dy * -10, y: dx * 10 })
    );
  };

  const handleMouseEnter = () => {
    setHovered(true);
    // shimmer sweep
    let pos = -100;
    const sweep = () => {
      pos += 6;
      setShimPos(pos);
      if (pos < 200) requestAnimationFrame(sweep);
    };
    requestAnimationFrame(sweep);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    setShimPos(-100);
    cancelAnimationFrame(frameRef.current);
    setTilt({ x: 0, y: 0 });
  };

  const enterFrom  = direction === 'next' ? 60 : -60;
  const transition = `opacity 0.45s cubic-bezier(.4,0,.2,1) ${delay}ms,
                      transform 0.45s cubic-bezier(.4,0,.2,1) ${delay}ms`;

  const cardStyle = {
    position: 'relative',
    overflow: 'hidden',
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    padding: '36px 28px 28px',
    borderRadius: 16,
    background: '#fff',
    boxShadow: hovered
      ? '0 24px 56px rgba(17,45,78,0.18), 0 6px 16px rgba(17,45,78,0.10)'
      : '0 6px 24px rgba(17,45,78,0.08)',
    cursor: 'pointer',
    opacity:   slideEntering ? 1 : 0,
    transform: slideEntering
      ? hovered
        ? `perspective(600px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(-8px) scale(1.03)`
        : 'perspective(600px) rotateX(0deg) rotateY(0deg) translateY(0) scale(1)'
      : `translateX(${enterFrom}px)`,
    transition,
    willChange: 'transform, opacity',
  };

  return (
    <Link
      ref={cardRef}
      to={`/services/${service.slug}`}
      style={cardStyle}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* shimmer sweep overlay */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `linear-gradient(105deg, transparent ${shimPos - 20}%, rgba(255,255,255,0.55) ${shimPos}%, transparent ${shimPos + 20}%)`,
        transition: 'none',
        zIndex: 2,
      }} />

      {/* top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 4,
        background: `linear-gradient(90deg, ${service.color}, #DBE2EF)`,
        transform: hovered ? 'scaleX(1)' : 'scaleX(0.3)',
        transformOrigin: 'left',
        transition: 'transform 0.4s ease',
        borderRadius: '16px 16px 0 0',
      }} />

      {/* icon bubble */}
      <div style={{
        width: 70, height: 70, borderRadius: '50%',
        background: `linear-gradient(135deg, ${service.color}22, ${service.color}44)`,
        border: `2px solid ${service.color}33`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '2rem',
        transform: hovered ? 'scale(1.15) rotate(-6deg)' : 'scale(1) rotate(0deg)',
        transition: 'transform 0.35s cubic-bezier(.34,1.56,.64,1)',
        boxShadow: hovered ? `0 8px 24px ${service.color}40` : 'none',
      }}>
        {service.icon}
      </div>

      <h4 style={{
        margin: 0, textAlign: 'center',
        color: 'var(--bj-espresso)',
        fontSize: '1.05rem',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'transform 0.3s ease',
      }}>
        {service.name}
      </h4>

      <p style={{
        margin: 0, fontSize: '0.88rem', textAlign: 'center',
        color: 'var(--bj-espresso)', opacity: 0.65, lineHeight: 1.6,
        transition: 'opacity 0.3s',
      }}>
        {service.blurb}
      </p>

      {/* CTA — slides up on hover */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        color: service.color, fontWeight: 700, fontSize: '0.85rem',
        opacity: hovered ? 1 : 0,
        transform: hovered ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
      }}>
        Explore service
        <span style={{
          transform: hovered ? 'translateX(4px)' : 'translateX(0)',
          transition: 'transform 0.3s ease',
          display: 'inline-block',
        }}>→</span>
      </div>
    </Link>
  );
}

export default function ServicesSlider() {
  const [current,  setCurrent]  = useState(0);
  const [entering, setEntering] = useState(true);
  const [direction, setDirection] = useState('next');
  const [paused,   setPaused]   = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef   = useRef(null);
  const progRef    = useRef(null);
  const startTime  = useRef(null);

  const goTo = useCallback((idx, dir = 'next') => {
    setEntering(false);
    setDirection(dir);
    setTimeout(() => {
      setCurrent(idx);
      setEntering(true);
      setProgress(0);
      startTime.current = performance.now();
    }, 80);
  }, []);

  const next = useCallback(() => goTo((current + 1) % TOTAL, 'next'), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + TOTAL) % TOTAL, 'prev'), [current, goTo]);

  // auto-play + progress bar
  useEffect(() => {
    if (paused) { cancelAnimationFrame(progRef.current); clearTimeout(timerRef.current); return; }

    startTime.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime.current;
      const pct     = Math.min((elapsed / AUTO_MS) * 100, 100);
      setProgress(pct);
      if (elapsed < AUTO_MS) {
        progRef.current = requestAnimationFrame(tick);
      } else {
        next();
      }
    };
    progRef.current = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(progRef.current); clearTimeout(timerRef.current); };
  }, [current, paused, next]);

  // keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const startIdx = current * VISIBLE;
  const visible = Array.from({ length: VISIBLE }, (_, i) => services[(startIdx + i) % services.length]);

  const ArrowBtn = ({ onClick, label, children }) => (
    <button
      onClick={onClick}
      aria-label={label}
      style={{
        width: 46, height: 46, borderRadius: '50%',
        border: '2px solid var(--bj-sand)',
        background: 'var(--bj-white)',
        cursor: 'pointer', fontSize: '1.1rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.2s ease',
        color: 'var(--bj-espresso)',
        boxShadow: '0 2px 8px rgba(17,45,78,0.08)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background   = 'var(--bj-clay)';
        e.currentTarget.style.color        = '#fff';
        e.currentTarget.style.borderColor  = 'var(--bj-clay)';
        e.currentTarget.style.transform    = 'scale(1.1)';
        e.currentTarget.style.boxShadow    = '0 6px 20px rgba(63,114,175,0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background   = 'var(--bj-white)';
        e.currentTarget.style.color        = 'var(--bj-espresso)';
        e.currentTarget.style.borderColor  = 'var(--bj-sand)';
        e.currentTarget.style.transform    = 'scale(1)';
        e.currentTarget.style.boxShadow    = '0 2px 8px rgba(17,45,78,0.08)';
      }}
    >
      {children}
    </button>
  );

  return (
    <section
      className="section"
      style={{ background: 'var(--bj-cream-soft)', overflow: 'hidden' }}
    >
      <div className="container">
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 36 }}>
          <div>
            <span className="eyebrow">What we offer</span>
            <h2 style={{ margin: '6px 0 0' }}>Every service for every journey</h2>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <ArrowBtn onClick={prev} label="Previous">←</ArrowBtn>
            <ArrowBtn onClick={next} label="Next">→</ArrowBtn>
          </div>
        </div>

        {/* Cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }} className="services-slider-grid">
          {visible.map((s, i) => (
            <ServiceCard
              key={`${current}-${s.slug}`}
              service={s}
              index={i}
              slideEntering={entering}
              direction={direction}
            />
          ))}
        </div>

        {/* Bottom controls: dots + progress */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginTop: 36 }}>
          {/* Slide counter */}


          {/* Progress track */}


          {/* Dot indicators */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {Array.from({ length: TOTAL }).map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > current ? 'next' : 'prev')}
                aria-label={`Go to slide ${i + 1}`}
                style={{
                  padding: 0, border: 'none', cursor: 'pointer',
                  width:  i === current ? 28 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === current ? 'var(--bj-clay)' : 'var(--bj-sand)',
                  transition: 'width 0.35s cubic-bezier(.4,0,.2,1), background 0.3s',
                }}
              />
            ))}
          </div>
        </div>

        {/* Keyboard hint */}
        <p style={{ textAlign: 'center', marginTop: 14, fontSize: '0.75rem', opacity: 0.35, letterSpacing: '0.04em' }}>
          Use ← → arrow keys to navigate · Hover to pause
        </p>
        <style>{`
        @media (max-width: 768px) {
          .services-slider-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .services-slider-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      </div>
    </section>
  );
}